import type { MessageThread } from '@emr/shared';

/**
 * Pure claim/close display rules for a message thread (`BACKLOG.md` W2, pillar 3) — extracted per
 * `claude-kit/rules/invariants.md`'s "business rules live in pure, tested modules, never inline in a
 * component" so `ThreadList.tsx`/`ThreadDetail.tsx` render a decision rather than recompute it, and
 * so the decision itself has a real test with no DOM (`threadRules.test.ts`, matching this repo's
 * `useMediaQuery.test.ts`/`topics.test.ts` precedent of a pure function tested independently of the
 * component that renders its answer).
 *
 * **Display-only — the server is still the real enforcement point.** `POST .../claim` and
 * `POST .../close` (`server/src/messaging/routes.ts`) re-check everything themselves; getting one of
 * these wrong here only means offering a control the server would then correctly refuse (a 409), not
 * a bypass of anything the server actually trusts a client to have decided.
 */

/** A `general`, open, unassigned thread — the only shape `POST .../claim` accepts
 * (`messaging/service.ts`'s `claimGeneralThread`: `UPDATE ... WHERE kind = 'general' AND
 * assigned_to_user_id IS NULL`). A `care_team` thread has no single owner by design and is never
 * claimable — `routes.ts`'s own `thread_not_claimable` refusal for exactly that case. */
export function isClaimable(thread: MessageThread): boolean {
  return thread.kind === 'general' && thread.status === 'open' && thread.assignedToUserId === null;
}

/** Is `viewerUserId` the staff member this `general` thread is currently assigned to? Always `false`
 * for a `care_team` thread, which never carries an `assignedToUserId` (`db/schema/messaging.ts`). */
export function isAssignedToViewer(thread: MessageThread, viewerUserId: string): boolean {
  return thread.assignedToUserId === viewerUserId;
}

/** `closeThread` (`messaging/service.ts`) accepts either kind — the only real gate is that a thread
 * already `closed` has nothing left to close. */
export function isClosable(thread: MessageThread): boolean {
  return thread.status === 'open';
}
